import React, { useCallback, useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { format } from 'date-fns';
import { toTitleCase } from '../qldWorkflow';
import Radio from './Radio';
import { trainers } from '../constants';

const pdfBlue = '#1670c9';
const pdfBorder = '#b9b9b9';
const pdfHeaderFill = '#e6e6e6';
const unitOfCompetency = 'CPCWHS1001 Prepare to Work Safely in the Construction Industry';
const aboutTrainingQuestions = [
    'I developed the skills expected from this training.',
    'I identified ways to build on my current knowledge and skills.',
    'The training focused on relevant skills.',
    'I developed the knowledge expected from this training',
    'The training prepared me well for work.',
    'I set high standards for myself in this training',
    'The training had a good mix of theory and practice.',
    'I looked for my own resources to help me learn',
    'Overall, I am satisfied with the training.',
    'I would recommend the training organisation to others.',
    'Training organisation staff respected my background and needs.',
    'I pushed myself to understand things I found confusing.',
    'Trainers had an excellent knowledge of the subject content.',
    'I received useful feedback on my assessments. ',
    'The way I was assessed was a fair test of my skills and knowledge.',
    'I learned to work with people.',
    'The training was at the right level of difficulty for me.',
    'The amount of work I had to do was reasonable.',
    'Assessments were based on realistic activities.',
    'It was always easy to know the standards expected.',
    'Training facilities and materials were in good condition',
    'I usually had a clear idea of what was expected of me.',
    'Trainers explained things clearly.',
    'The training organisation had a range of services to support learners.',
    'I learned to plan and manage my work.',
    'The training used up-to-date equipment, facilities and materials.',
    'I approached trainers if I needed help.',
    'Trainers made the subject as interesting as possible.',
    'I would recommend the training to others.',
    'The training organisation gave appropriate recognition of existing knowledge and skills',
    'Training resources were available when I needed them.',
    'I was given enough material to keep up my interest.',
    'The training was flexible enough to meet my needs.',
    'Trainers encouraged learners to ask questions.',
    'Trainers made it clear right from the start what they expected from me.'
];
const aboutTrainingOptions = ['Strongly agree', 'Agree', 'Disagree', 'Strongly disagree'];
const yesNoQuestions = [
    'Are you undertaking an APPRENTICESHIP Or TRAINEESHIP?',
    'Did you get any RECOGNITION OF PRIOR LEARNING towards your training such as subject exemptions, course credits or advanced standing?',
    'Do you speak a LANGUAGE OTHER THAN ENGLISH at home? ',
    'Are you a PERMANENT RESIDENT Or CITIZEN of Australia?',
    'Do you consider yourself to have a DISABILITY, IMPAIRMENT, or LONG-TERM CONDITION',
];
const qualificationOptions = [
    'Certificate I',
    'Certificate II',
    'Certificate III',
    'Certificate IV',
    'Certificate level unknown',
    'Diploma',
    'Advanced diploma',
    'Associate degree',
    'Degree',
    'Short course or statement of attainment',
    'VET graduate certificate or graduate diploma',
    'Other qualification or training',
    'Do not know'
];
const broadFieldOptions = [
    ' Natural and physical sciences',
    'Information technology',
    'Engineering and related technologies',
    'Architecture and building',
    'Agriculture, environmental and related studies',
    'Health',
    'Education',
    'Management and commerce',
    'Society and culture',
    'Creative arts',
    'Food, hospitality and personal services',
    'Other'
];
const genderOptions = ['Female/Woman', 'Male/Man', 'Non-binary', 'Prefer Not to Answer'];
const ageOptions = ['Under 15', '15 to 19', '20 to 24', '25 to 34', '35 to 44', '45 to 54', '55 to 64', '65 or over'];
const aboriginalOptions = ['No', 'Yes, Aboriginal', 'Yes, Torres Strait Islander', 'Yes, both Aboriginal and Torres Strait Islander'];

const trainerRows = [
    { satisfactoryKey: 'assessment1Satisfactory', notSatisfactoryKey: 'assessment1NotSatisfactory', code: 'AT1', label: 'Multiple choice / Short Answer Questions' },
    { satisfactoryKey: 'assessment2Satisfactory', notSatisfactoryKey: 'assessment2NotSatisfactory', code: 'AT2', label: 'Oral Questions' },
    { satisfactoryKey: 'assessment3Satisfactory', notSatisfactoryKey: 'assessment3NotSatisfactory', code: 'AT3', label: 'Complete a Simple Form' },
    { satisfactoryKey: 'assessment4Satisfactory', notSatisfactoryKey: 'assessment4NotSatisfactory', code: 'AT4', label: 'Fitting PPE (Observation)' },
];

const buildDefaultTrainerAssessmentStatus = () => (
    trainerRows.reduce((acc, row) => ({
        ...acc,
        [row.satisfactoryKey]: true,
        [row.notSatisfactoryKey]: false,
    }), {})
);

const declarationStatement = 'I declare this assessment is my own work and I have been provided with feedback from the trainer/assessor.';
const assessorDeclarationStatement = 'I declare that I have conducted a fair and valid assessment, and the student has been provided with feedback.';
const getTrainerSignatureAssetPath = (trainerId = '') => {
    const trainer = trainers.find((row) => row.trainerId === trainerId);
    const filename = trainer?.sign?.split('/').pop();

    return filename ? `/trainer-signatures/${filename}` : '';
};

const sectionCellStyles = {
    border: `1px solid ${pdfBorder}`,
    padding: '12px 14px',
    verticalAlign: 'top',
};

const inputLikeStyles = {
    width: '100%',
    border: 0,
    outline: 'none',
    background: 'transparent',
    fontSize: '17px',
    color: '#111',
    padding: 0,
    fontFamily: 'inherit',
};

const feedbackStyles = {
    width: '100%',
    minHeight: '220px',
    border: `1px solid ${pdfBorder}`,
    outline: 'none',
    resize: 'none',
    backgroundImage: 'repeating-linear-gradient(to bottom, transparent 0, transparent 51px, #e2e2e2 52px)',
    lineHeight: '52px',
    fontSize: '17px',
    padding: '0 12px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    backgroundColor: '#fff',
    borderRadius: '8px',
};

const choiceRowStyles = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px 18px',
    alignItems: 'center',
};

const choiceItemStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '16px',
    color: '#111',
};

const optionTableStyles = {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    marginBottom: '16px',
};

const inlineErrorStyles = {
    margin: '6px 0 0',
    fontSize: '13px',
    color: '#c62828',
    fontWeight: '600',
};

const selectStyles = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: `1px solid ${pdfBorder}`,
    backgroundColor: '#fff',
    color: '#111',
    fontSize: '16px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
};

const renderCheckboxCell = (checked, onToggle, disabled = false) => (
    <button
        type='button'
        onClick={disabled ? undefined : onToggle}
        style={{
            width: '30px',
            height: '30px',
            border: `1px solid ${pdfBorder}`,
            backgroundColor: checked ? '#eef5ff' : '#fff',
            color: '#111',
            fontSize: '21px',
            lineHeight: 1,
            cursor: disabled ? 'default' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
        }}
    >
        {checked ? '✓' : ''}
    </button>
);

const renderChoiceRowField = (label, value, onChange, options, disabled = false, error = false) => (
    <div style={{ marginBottom: '16px' }}>
        <p style={{ marginTop: 0, marginBottom: '8px', fontWeight: '600' }}>{label}</p>
        <div style={{ ...choiceRowStyles, border: `1px solid ${error ? '#c62828' : pdfBorder}`, borderRadius: '8px', padding: '12px 14px', backgroundColor: disabled ? '#f8f8f8' : '#fff' }}>
            {options.map((option) => (
                <div key={option} style={choiceItemStyles}>
                    <input
                        type='radio'
                        checked={value === option}
                        onChange={disabled ? undefined : () => onChange(option)}
                        disabled={disabled}
                    />
                    <label>{option}</label>
                </div>
            ))}
        </div>
    </div>
);

const renderSelectField = (label, value, onChange, options, disabled = false, error = false) => (
    <div style={{ marginBottom: '16px' }}>
        <p style={{ marginTop: 0, marginBottom: '8px', fontWeight: '600' }}>{label}</p>
        <select
            value={value || ''}
            onChange={({ target }) => onChange(target.value)}
            disabled={disabled}
            style={{ ...selectStyles, borderColor: error ? '#c62828' : pdfBorder, backgroundColor: disabled ? '#f8f8f8' : '#fff' }}
        >
            <option value=''>Select</option>
            {options.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
    </div>
);

const renderOptionTableField = ({
    heading,
    questions,
    answers,
    setAnswers,
    options,
    disabled = false,
    error = false,
    firstColumnHeader = 'Question',
}) => (
    <div style={{ marginBottom: '20px' }}>
        {heading ? <p style={{ marginTop: 0, marginBottom: '12px', fontWeight: '600' }}>{heading}</p> : null}
        <table style={{ ...optionTableStyles, border: `1px solid ${error ? '#c62828' : pdfBorder}` }}>
            <thead>
                <tr>
                    <th style={{ ...sectionCellStyles, width: '58%', textAlign: 'left', backgroundColor: '#fff', fontWeight: '700' }}>{firstColumnHeader}</th>
                    {options.map((option) => (
                        <th key={option} style={{ ...sectionCellStyles, width: `${42 / options.length}%`, textAlign: 'center', backgroundColor: '#fff', fontWeight: '700' }}>
                            {option}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {questions.map((question, index) => (
                    <tr key={`${firstColumnHeader}-${index}`}>
                        <td style={{ ...sectionCellStyles, textAlign: 'left' }}>{question}</td>
                        {options.map((option) => (
                            <td key={`${question}-${option}`} style={{ ...sectionCellStyles, textAlign: 'center' }}>
                                <input
                                    type='radio'
                                    checked={answers[index] === option}
                                    onChange={disabled ? undefined : () => setAnswers(answers.map((value, answerIndex) => (answerIndex === index ? option : value)))}
                                    disabled={disabled}
                                />
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const QldDeclarationStep = ({
    trainerMode = false,
    studentDoc,
    initialData = {},
    trainerName = '',
    onProgress,
    onComplete,
}) => {
    const studentSignatureRef = useRef();
    const lastAppliedStudentSignatureRef = useRef('');

    const [studentName, setStudentName] = useState('');
    const [assessmentAddress, setAssessmentAddress] = useState('');
    const [assessmentDate, setAssessmentDate] = useState('');
    const [trainerAssessorName, setTrainerAssessorName] = useState('');
    const [feedbackToStudent, setFeedbackToStudent] = useState('');
    const [studentDeclarationDate, setStudentDeclarationDate] = useState('');
    const [trainerDeclarationDate, setTrainerDeclarationDate] = useState('');
    const [studentSignature, setStudentSignature] = useState('');
    const [trainerSignature, setTrainerSignature] = useState('');
    const [trainerAssessmentStatus, setTrainerAssessmentStatus] = useState({});
    const [aboutTrainingAnswers, setAboutTrainingAnswers] = useState([]);
    const [bestAspect, setBestAspect] = useState('');
    const [improve, setImprove] = useState('');
    const [qualification, setQualification] = useState('');
    const [broadField, setBroadField] = useState('');
    const [trainingTitle, setTrainingTitle] = useState('');
    const [startTraining, setStartTraining] = useState('');
    const [gender, setGender] = useState('');
    const [age, setAge] = useState('');
    const [aboriginal, setAboriginal] = useState('');
    const [yesNoAnswers, setYesNoAnswers] = useState([]);
    const [postcode, setPostcode] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [errors, setErrors] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');

    const hasError = (fieldName) => errors.includes(fieldName);
    const renderInlineError = (fieldName, message = 'This field is required.') => (
        hasError(fieldName) ? <p style={inlineErrorStyles}>{message}</p> : null
    );

    useEffect(() => {
        const nextAboutTrainingAnswers = Array.isArray(initialData.aboutTrainingAnswers) && initialData.aboutTrainingAnswers.length === aboutTrainingQuestions.length
            ? initialData.aboutTrainingAnswers
            : [...Array(aboutTrainingQuestions.length)].map(() => '');
        const nextYesNoAnswers = Array.isArray(initialData.yesNoAnswers) && initialData.yesNoAnswers.length === yesNoQuestions.length
            ? initialData.yesNoAnswers
            : [...Array(yesNoQuestions.length)].map(() => '');

        setStudentName(toTitleCase(initialData.studentName || `${studentDoc?.firstName || ''} ${studentDoc?.lastName || ''}`.trim()));
        setAssessmentAddress(toTitleCase(initialData.assessmentAddress || 'Online'));
        setAssessmentDate(initialData.assessmentDate || format(new Date(), 'dd/MM/yyyy'));
        setTrainerAssessorName(toTitleCase(initialData.trainerAssessorName || trainerName));
        setFeedbackToStudent(initialData.feedbackToStudent || '');
        setStudentDeclarationDate(initialData.studentDeclarationDate || format(new Date(), 'dd/MM/yyyy'));
        setTrainerDeclarationDate(initialData.trainerDeclarationDate || format(new Date(), 'dd/MM/yyyy'));
        setStudentSignature(initialData.studentSignature || '');
        setTrainerSignature(initialData.trainerSignature || getTrainerSignatureAssetPath(studentDoc?.trainerId));
        const hasTrainerAssessmentStatus = Boolean(initialData.trainerAssessmentStatus && Object.keys(initialData.trainerAssessmentStatus).length > 0);
        setTrainerAssessmentStatus(
            trainerMode
                ? (hasTrainerAssessmentStatus ? initialData.trainerAssessmentStatus : buildDefaultTrainerAssessmentStatus())
                : (initialData.trainerAssessmentStatus || {})
        );
        setAboutTrainingAnswers(nextAboutTrainingAnswers);
        setBestAspect(initialData.bestAspect || '');
        setImprove(initialData.improve || '');
        setQualification(initialData.qualification || '');
        setBroadField(initialData.broadField || '');
        setTrainingTitle(initialData.trainingTitle || '');
        setStartTraining(initialData.startTraining || '');
        setGender(initialData.gender || '');
        setAge(initialData.age || '');
        setAboriginal(initialData.aboriginal || '');
        setYesNoAnswers(nextYesNoAnswers);
        setPostcode(initialData.postcode || '');
        setCurrentPage(trainerMode ? 1 : 0);
    }, [initialData, studentDoc, trainerMode, trainerName]);

    useEffect(() => {
        if (!studentSignatureRef.current) {
            return;
        }

        if (!studentSignature) {
            lastAppliedStudentSignatureRef.current = '';
            studentSignatureRef.current.clear();
            return;
        }

        if (lastAppliedStudentSignatureRef.current === studentSignature) {
            return;
        }

        try {
            studentSignatureRef.current.clear();
            studentSignatureRef.current.fromDataURL(studentSignature);
            lastAppliedStudentSignatureRef.current = studentSignature;
        } catch (error) {
            console.error('Failed to restore student declaration signature', error);
        }
    }, [studentSignature]);

    const overallCompetent = trainerRows.every((row) => trainerAssessmentStatus[row.satisfactoryKey]);
    const overallNotYetCompetent = trainerRows.some((row) => trainerAssessmentStatus[row.notSatisfactoryKey]);

    const getCanvasSignatureDataUrl = (ref, fallbackValue = '') => {
        const signaturePad = ref.current;
        const canvas = signaturePad?._canvas;

        if (!signaturePad || !canvas || canvas.width === 0 || canvas.height === 0) {
            return fallbackValue;
        }

        try {
            if (signaturePad.isEmpty()) {
                return fallbackValue;
            }

            return signaturePad.getTrimmedCanvas().toDataURL('image/png');
        } catch (error) {
            console.error('Failed to read declaration signature canvas', error);
            return fallbackValue;
        }
    };

    const hasCanvasSignature = (ref, fallbackValue = '') => (
        Boolean((ref.current && !ref.current.isEmpty()) || fallbackValue)
    );

    const buildPayload = useCallback(({ includeSignatures = true } = {}) => ({
        studentName,
        assessmentAddress: toTitleCase(assessmentAddress),
        assessmentDate,
        trainerAssessorName: toTitleCase(trainerAssessorName),
        feedbackToStudent,
        studentDeclarationDate,
        trainerDeclarationDate,
        studentSignature: includeSignatures
            ? getCanvasSignatureDataUrl(studentSignatureRef, studentSignature)
            : studentSignature,
        trainerSignature,
        trainerAssessmentStatus,
        aboutTrainingAnswers,
        bestAspect,
        improve,
        qualification,
        broadField,
        trainingTitle,
        startTraining,
        gender,
        age,
        aboriginal,
        yesNoAnswers,
        postcode,
    }), [
        studentName,
        assessmentAddress,
        assessmentDate,
        trainerAssessorName,
        feedbackToStudent,
        studentDeclarationDate,
        trainerDeclarationDate,
        studentSignature,
        trainerSignature,
        trainerAssessmentStatus,
        aboutTrainingAnswers,
        bestAspect,
        improve,
        qualification,
        broadField,
        trainingTitle,
        startTraining,
        gender,
        age,
        aboriginal,
        yesNoAnswers,
        postcode,
    ]);

    useEffect(() => {
        if (!onProgress) {
            return undefined;
        }

        const timeoutId = setTimeout(() => {
            onProgress(buildPayload({ includeSignatures: false }));
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [
        onProgress,
        studentName,
        assessmentAddress,
        assessmentDate,
        trainerAssessorName,
        feedbackToStudent,
        studentDeclarationDate,
        trainerDeclarationDate,
        studentSignature,
        trainerSignature,
        trainerAssessmentStatus,
        aboutTrainingAnswers,
        bestAspect,
        improve,
        qualification,
        broadField,
        trainingTitle,
        startTraining,
        gender,
        age,
        aboriginal,
        yesNoAnswers,
        postcode,
    ]);

    const validateQiPage = () => {
        const tempErrors = [];

        if (aboutTrainingAnswers.some((value) => !String(value || '').trim())) {
            tempErrors.push('aboutTrainingAnswers');
        }
        if (!bestAspect.trim()) {
            tempErrors.push('bestAspect');
        }
        if (!improve.trim()) {
            tempErrors.push('improve');
        }
        if (!qualification.trim()) {
            tempErrors.push('qualification');
        }
        if (!broadField.trim()) {
            tempErrors.push('broadField');
        }
        if (!trainingTitle.trim()) {
            tempErrors.push('trainingTitle');
        }
        if (!startTraining.trim()) {
            tempErrors.push('startTraining');
        }
        if (!gender.trim()) {
            tempErrors.push('gender');
        }
        if (!age.trim()) {
            tempErrors.push('age');
        }
        if (!aboriginal.trim()) {
            tempErrors.push('aboriginal');
        }
        if (yesNoAnswers.some((value) => !String(value || '').trim())) {
            tempErrors.push('yesNoAnswers');
        }
        if (!postcode.trim()) {
            tempErrors.push('postcode');
        }

        if (tempErrors.length > 0) {
            setErrors(tempErrors);
            setErrorMessage('Please complete all quality indicator questions.');
            return false;
        }

        setErrors([]);
        setErrorMessage('');
        return true;
    };

    const validateStudent = () => {
        const tempErrors = [];
        if (!studentName.trim()) {
            tempErrors.push('studentName');
        }
        if (!hasCanvasSignature(studentSignatureRef, studentSignature)) {
            tempErrors.push('studentSignature');
        }
        if (!studentDeclarationDate.trim()) {
            tempErrors.push('studentDeclarationDate');
        }
        if (tempErrors.length > 0) {
            setErrors(tempErrors);
            setErrorMessage('Please complete the student declaration.');
            return false;
        }
        return true;
    };

    const validateTrainer = () => {
        const tempErrors = [];
        trainerRows.forEach((row) => {
            if (!trainerAssessmentStatus[row.satisfactoryKey] && !trainerAssessmentStatus[row.notSatisfactoryKey]) {
                tempErrors.push(row.satisfactoryKey);
            }
        });
        if (!trainerSignature) {
            tempErrors.push('trainerSignature');
        }
        if (!trainerDeclarationDate.trim()) {
            tempErrors.push('trainerDeclarationDate');
        }
        if (!overallCompetent && !overallNotYetCompetent) {
            tempErrors.push('overallResult');
        }
        if (tempErrors.length > 0) {
            setErrors(tempErrors);
            setErrorMessage('Please complete the assessor record and declaration.');
            return false;
        }
        return true;
    };

    const handleSubmit = () => {
        const isValid = trainerMode ? validateTrainer() : validateStudent();
        if (!isValid) {
            return;
        }

        setErrors([]);
        setErrorMessage('');
        onComplete && onComplete(
            buildPayload(),
            trainerMode ? { decision: 'passed' } : undefined
        );
    };

    const handleNextPage = () => {
        if (!trainerMode && !validateQiPage()) {
            return;
        }

        setErrors([]);
        setErrorMessage('');
        setCurrentPage(1);
    };

    const handlePrevPage = () => {
        setErrors([]);
        setErrorMessage('');
        setCurrentPage(0);
    };

    const toggleTrainerStatus = (keyToSet, oppositeKey) => {
        setTrainerAssessmentStatus((prev) => ({
            ...prev,
            [keyToSet]: !prev[keyToSet],
            [oppositeKey]: false,
        }));
        setErrors((prev) => prev.filter((field) => field !== keyToSet && field !== oppositeKey && field !== 'overallResult'));
    };

    return (
        <div className='form-wrapper qld-stage-shell'>
            <div style={{ borderTop: '2px solid #c9d9ee', paddingTop: '18px' }}>
                {currentPage === 0 ? (
                    <>
                        <h1 style={{ color: pdfBlue, marginBottom: '18px', fontSize: '57px', fontWeight: '400', lineHeight: 1.05 }}>Quality Indicator Questions</h1>
                        <p style={{ marginTop: 0, fontSize: '19px' }}>Please complete the quality indicator questions before proceeding to the declaration.</p>

                        <div style={{ marginTop: '24px' }}>
                            <h2 style={{ marginTop: 0, marginBottom: '18px', fontSize: '25px' }}>About Your Training</h2>
                            {renderOptionTableField({
                                questions: aboutTrainingQuestions,
                                answers: aboutTrainingAnswers,
                                setAnswers: setAboutTrainingAnswers,
                                options: aboutTrainingOptions,
                                disabled: trainerMode,
                                error: errors.includes('aboutTrainingAnswers'),
                                firstColumnHeader: 'Statement',
                            })}

                            {renderInlineError('aboutTrainingAnswers', 'Please answer every row in this table.')}

                            <div style={{ marginBottom: '16px' }}>
                                <p style={{ marginTop: 0, marginBottom: '8px', fontWeight: '600' }}>What were the BEST ASPECTS of the training?</p>
                                <textarea value={bestAspect} onChange={({ target }) => setBestAspect(target.value)} readOnly={trainerMode} style={{ ...feedbackStyles, minHeight: '140px', lineHeight: '32px', backgroundImage: 'repeating-linear-gradient(to bottom, transparent 0, transparent 31px, #e2e2e2 32px)' }} />
                            </div>
                            <div style={{ marginBottom: '16px' }}>
                                <p style={{ marginTop: 0, marginBottom: '8px', fontWeight: '600' }}>What aspects of the training were MOST IN NEED OF IMPROVEMENT?</p>
                                <textarea value={improve} onChange={({ target }) => setImprove(target.value)} readOnly={trainerMode} style={{ ...feedbackStyles, minHeight: '140px', lineHeight: '32px', backgroundImage: 'repeating-linear-gradient(to bottom, transparent 0, transparent 31px, #e2e2e2 32px)' }} />
                            </div>

                            <h2 style={{ marginTop: '28px', marginBottom: '18px', fontSize: '25px' }}>Your Training Details</h2>
                            {renderSelectField('What TYPE OF QUALIFICATION are you currently enrolled in?', qualification, setQualification, qualificationOptions, trainerMode, errors.includes('qualification'))}
                            {renderSelectField('What is the BROAD FIELD of your current training?', broadField, setBroadField, broadFieldOptions, trainerMode, errors.includes('broadField'))}
                            <div style={{ marginBottom: '16px' }}>
                                <p style={{ marginTop: 0, marginBottom: '8px', fontWeight: '600' }}>What is the FULL TITLE of your current qualification or training?</p>
                                <textarea value={trainingTitle} onChange={({ target }) => setTrainingTitle(target.value)} readOnly={trainerMode} style={{ ...feedbackStyles, minHeight: '100px', lineHeight: '32px', backgroundImage: 'repeating-linear-gradient(to bottom, transparent 0, transparent 31px, #e2e2e2 32px)' }} />
                            </div>
                            <div style={{ marginBottom: '16px' }}>
                                <p style={{ marginTop: 0, marginBottom: '8px', fontWeight: '600' }}>In what MONTH AND YEAR did you start your current training?</p>
                                <input type='text' value={startTraining} onChange={({ target }) => setStartTraining(target.value)} readOnly={trainerMode} style={{ ...inputLikeStyles, border: `1px solid ${errors.includes('startTraining') ? '#c62828' : pdfBorder}`, padding: '10px 12px', borderRadius: '8px', backgroundColor: trainerMode ? '#f8f8f8' : '#fff' }} />
                            </div>

                            <h2 style={{ marginTop: '28px', marginBottom: '18px', fontSize: '25px' }}>About You</h2>
                            {renderSelectField('What is your age in years?', age, setAge, ageOptions, trainerMode, errors.includes('age'))}
                            {renderSelectField('What is your gender?', gender, setGender, genderOptions, trainerMode, errors.includes('gender'))}
                            {renderSelectField('Are you of ABORIGINAL or TORRES STRAIT ISLANDER origin?', aboriginal, setAboriginal, aboriginalOptions, trainerMode, errors.includes('aboriginal'))}
                            {renderOptionTableField({
                                questions: yesNoQuestions,
                                answers: yesNoAnswers,
                                setAnswers: setYesNoAnswers,
                                options: ['Yes', 'No'],
                                disabled: trainerMode,
                                error: errors.includes('yesNoAnswers'),
                                firstColumnHeader: 'Question',
                            })}
                            {renderInlineError('yesNoAnswers', 'Please answer every row in this table.')}
                            <div style={{ marginBottom: '16px' }}>
                                <p style={{ marginTop: 0, marginBottom: '8px', fontWeight: '600' }}>What is the POSTCODE of your main place of residence?</p>
                                <input type='text' value={postcode} onChange={({ target }) => setPostcode(target.value)} readOnly={trainerMode} style={{ ...inputLikeStyles, border: `1px solid ${errors.includes('postcode') ? '#c62828' : pdfBorder}`, padding: '10px 12px', borderRadius: '8px', backgroundColor: trainerMode ? '#f8f8f8' : '#fff' }} />
                            </div>
                        </div>

                        {errorMessage && <p style={{ color: '#c62828', fontWeight: '600', marginTop: '16px' }}>{errorMessage}</p>}
                        <div style={{ display: 'flex', marginTop: '20px' }}>
                            <button style={{ marginLeft: 'auto', display: 'flex' }} onClick={handleNextPage}>
                                Next
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                <h1 style={{ color: pdfBlue, marginBottom: '18px', fontSize: '65px', fontWeight: '400', lineHeight: 1.05 }}>{trainerMode ? 'Student Declaration' : 'Record of Assessment Outcome'}</h1>
                <p style={{ marginTop: 0, fontSize: '19px' }}>
                    {trainerMode
                        ? 'This page is to be signed and dated by the student and completed by the assessor to record the overall judgement on the student&apos;s competence.'
                        : 'Please review and complete the student declaration below.'}
                </p>
                {trainerMode && <p style={{ marginTop: 0, fontSize: '19px' }}>A student can only be deemed competent when each of the assessment tasks listed below has been completed to a satisfactory standard.</p>}

                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', marginTop: '20px' }}>
                    <tbody>
                        <tr>
                            <td style={{ ...sectionCellStyles, width: '24%', fontWeight: '700' }}>Student Name:</td>
                            <td style={sectionCellStyles}>
                                <input type='text' value={studentName} onChange={({ target }) => setStudentName(target.value)} style={inputLikeStyles} disabled={!trainerMode} />
                            </td>
                        </tr>
                        <tr>
                            <td style={{ ...sectionCellStyles, fontWeight: '700' }}>Trainer/s / Assessor/s:</td>
                            <td style={sectionCellStyles}>
                                <input type='text' value={trainerAssessorName} onChange={({ target }) => setTrainerAssessorName(target.value)} style={inputLikeStyles} disabled={!trainerMode} />
                            </td>
                        </tr>
                        <tr>
                            <td style={{ ...sectionCellStyles, fontWeight: '700' }}>Assessment Date/s:</td>
                            <td style={sectionCellStyles}>
                                <input type='text' value={assessmentDate} onChange={({ target }) => setAssessmentDate(target.value)} style={inputLikeStyles} disabled={!trainerMode} />
                            </td>
                        </tr>
                        <tr>
                            <td style={{ ...sectionCellStyles, fontWeight: '700' }}>Assessment Address:</td>
                            <td style={sectionCellStyles}>
                                <input type='text' value={assessmentAddress} onChange={({ target }) => setAssessmentAddress(target.value)} style={inputLikeStyles} disabled={!trainerMode} />
                            </td>
                        </tr>
                        <tr>
                            <td style={{ ...sectionCellStyles, fontWeight: '700' }}>Unit of Competency:</td>
                            <td style={{ ...sectionCellStyles, fontWeight: '700' }}>{unitOfCompetency}</td>
                        </tr>
                    </tbody>
                </table>

                {trainerMode && <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', marginTop: '-1px' }}>
                    <tbody>
                        <tr style={{ backgroundColor: pdfHeaderFill }}>
                            <td style={{ ...sectionCellStyles, fontWeight: '700' }} colSpan={2}>Tasks Completed:</td>
                            <td style={{ ...sectionCellStyles, fontWeight: '700' }} colSpan={4}>Result:</td>
                        </tr>
                        {trainerRows.map((row) => (
                            <tr key={row.code}>
                                <td style={{ ...sectionCellStyles, width: '7%', fontWeight: '700' }}>{row.code}</td>
                                <td style={{ ...sectionCellStyles, width: '43%' }}>{row.label}</td>
                                <td style={{ ...sectionCellStyles, width: '18%' }}>Satisfactory</td>
                                <td style={{ ...sectionCellStyles, width: '9%', textAlign: 'center' }}>
                                    {renderCheckboxCell(
                                        !!trainerAssessmentStatus[row.satisfactoryKey],
                                        () => toggleTrainerStatus(row.satisfactoryKey, row.notSatisfactoryKey),
                                        !trainerMode
                                    )}
                                </td>
                                <td style={{ ...sectionCellStyles, width: '18%' }}>Not satisfactory</td>
                                <td style={{ ...sectionCellStyles, width: '9%', textAlign: 'center' }}>
                                    {renderCheckboxCell(
                                        !!trainerAssessmentStatus[row.notSatisfactoryKey],
                                        () => toggleTrainerStatus(row.notSatisfactoryKey, row.satisfactoryKey),
                                        !trainerMode
                                    )}
                                </td>
                            </tr>
                        ))}
                        <tr>
                            <td style={{ ...sectionCellStyles, fontWeight: '700' }} colSpan={2} rowSpan={2}>Final Assessment Result</td>
                            <td style={{ ...sectionCellStyles }} colSpan={3}>Competent</td>
                            <td style={{ ...sectionCellStyles, textAlign: 'center' }}>
                                {renderCheckboxCell(overallCompetent, () => {}, true)}
                            </td>
                        </tr>
                        <tr>
                            <td style={{ ...sectionCellStyles }} colSpan={3}>Not Yet Competent</td>
                            <td style={{ ...sectionCellStyles, textAlign: 'center' }}>
                                {renderCheckboxCell(overallNotYetCompetent, () => {}, true)}
                            </td>
                        </tr>
                        <tr style={{ backgroundColor: pdfHeaderFill }}>
                            <td style={{ ...sectionCellStyles, fontWeight: '700' }} colSpan={6}>Feedback to Student:</td>
                        </tr>
                        <tr>
                            <td style={{ ...sectionCellStyles, padding: 0 }} colSpan={6}>
                                <textarea
                                    value={feedbackToStudent}
                                    onChange={({ target }) => setFeedbackToStudent(target.value)}
                                    style={feedbackStyles}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>}

                <div style={{ display: 'grid', gridTemplateColumns: trainerMode ? '1fr 1fr' : '1fr', gap: 0, marginTop: '28px' }}>
                    <div style={{ border: `1px solid ${pdfBorder}`, borderRight: 0 }}>
                        <div style={{ ...sectionCellStyles, fontWeight: '700' }}>Student Declaration:</div>
                        <div style={sectionCellStyles}>{declarationStatement}</div>
                        <div style={{ ...sectionCellStyles, minHeight: '220px' }}>
                            <div style={{ marginBottom: '12px', fontSize: '17px', fontWeight: '700' }}>Student signature:</div>
                            <div style={{ width: '100%', display: trainerMode ? 'none' : 'block' }}>
                                <SignatureCanvas
                                    penColor='black'
                                    ref={studentSignatureRef}
                                    canvasProps={{
                                        className: `sigCanvas ${errors.includes('studentSignature') ? 'error' : ''}`,
                                        style: {
                                            width: '100%',
                                            maxWidth: '100%',
                                            height: '120px',
                                            border: `1px solid ${errors.includes('studentSignature') ? '#c62828' : '#d9d9d9'}`,
                                            display: 'block',
                                        },
                                    }}
                                    clearOnResize={false}
                                />
                            </div>
                            {trainerMode && (
                                <div style={{ minHeight: '120px', border: `1px solid ${pdfBorder}`, backgroundColor: '#fff' }}>
                                    {studentSignature && <img src={studentSignature} alt='Student signature' style={{ width: '100%', height: '120px', objectFit: 'contain' }} />}
                                </div>
                            )}
                            {!trainerMode && (
                                <button
                                    type='button'
                                    className='textButton'
                                    style={{ display: 'flex', marginTop: '10px', marginLeft: 0 }}
                                    onClick={() => {
                                        studentSignatureRef.current?.clear();
                                        setStudentSignature('');
                                    }}
                                >
                                    Clear Signature
                                </button>
                            )}
                            <div style={{ marginTop: '18px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                <div style={{ fontSize: '17px', fontWeight: '700' }}>Date:</div>
                                <div style={{ flex: '1 1 180px' }}>
                                    {trainerMode ? studentDeclarationDate : (
                                        <input
                                            type='text'
                                            value={studentDeclarationDate}
                                            onChange={({ target }) => setStudentDeclarationDate(target.value)}
                                            style={inputLikeStyles}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {trainerMode && <div style={{ border: `1px solid ${pdfBorder}` }}>
                        <div style={{ ...sectionCellStyles, fontWeight: '700' }}>Assessor Declaration:</div>
                        <div style={sectionCellStyles}>{assessorDeclarationStatement}</div>
                        <div style={{ ...sectionCellStyles, minHeight: '220px' }}>
                            <div style={{ marginBottom: '12px', fontSize: '17px', fontWeight: '700' }}>Assessor signature:</div>
                            <div style={{ minHeight: '120px', border: `1px solid ${errors.includes('trainerSignature') ? '#c62828' : pdfBorder}`, backgroundColor: '#fff' }}>
                                {trainerSignature && <img src={trainerSignature} alt='Assessor signature' style={{ width: '100%', height: '120px', objectFit: 'contain' }} />}
                            </div>
                            {renderInlineError('trainerSignature', 'Trainer signature is not configured.')}
                            <div style={{ marginTop: '18px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                <div style={{ fontSize: '17px', fontWeight: '700' }}>Date:</div>
                                <div style={{ flex: '1 1 180px' }}>
                                    {trainerMode ? (
                                        <input
                                            type='text'
                                            value={trainerDeclarationDate}
                                            onChange={({ target }) => setTrainerDeclarationDate(target.value)}
                                            style={inputLikeStyles}
                                        />
                                    ) : trainerDeclarationDate}
                                </div>
                            </div>
                        </div>
                    </div>}
                </div>

                {errorMessage && <p style={{ color: '#c62828', fontWeight: '600', marginTop: '16px' }}>{errorMessage}</p>}

                <div style={{ display: 'flex', marginTop: '20px' }}>
                    {!trainerMode && (
                        <button style={{ color: '#111', background: '#fff', border: `1px solid ${pdfBorder}` }} onClick={handlePrevPage}>
                            Back
                        </button>
                    )}
                    <button style={{ marginLeft: 'auto', display: 'flex' }} onClick={handleSubmit}>
                        {trainerMode ? 'Mark as Completed' : 'Submit Declaration'}
                    </button>
                </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default QldDeclarationStep;
